import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { Loader2, FileWarning, AlertTriangle, ArrowRight, BookOpen, FileText } from 'lucide-react';

export function DocumentAlertsWidget() {
  const { t, i18n } = useTranslation('dashboard');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const { data, isLoading, error } = trpc.dashboard.documentExpiryAlerts.useQuery({ days: 30 });

  if (isLoading) {
    return (
      <div className="luxury-card h-[320px] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--muted-foreground))]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="luxury-card h-[320px] flex items-center justify-center">
        <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('chartError')}</p>
      </div>
    );
  }

  const allAlerts = [
    ...(data?.documents || []),
    ...(data?.passports || []),
  ].sort((a, b) => (a.daysUntilExpiry || 0) - (b.daysUntilExpiry || 0));

  return (
    <div className="luxury-card p-0 overflow-hidden">
      <div className="flex items-center justify-between border-b border-[hsl(var(--border))] px-6 py-4">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <FileWarning className="h-5 w-5 text-amber-500" />
            {t('documentAlerts')}
          </h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('expiringIn30Days')}</p>
        </div>
        {data && data.totalAlerts > 0 && (
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center h-8 w-8 rounded-full bg-amber-100 text-amber-700 font-semibold text-sm">
              {data.totalAlerts}
            </span>
          </div>
        )}
      </div>

      {!data || data.totalAlerts === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 px-6">
          <div className="rounded-2xl bg-emerald-50 p-5">
            <FileText className="h-10 w-10 text-emerald-600" />
          </div>
          <p className="mt-4 font-display font-medium text-emerald-700">{t('noDocumentAlerts')}</p>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))] text-center">
            {t('allDocumentsValid')}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-[hsl(var(--border))] max-h-[220px] overflow-y-auto">
          {allAlerts.slice(0, 5).map((alert, index) => {
            const isUrgent = (alert.daysUntilExpiry || 0) <= 7;
            const isPassport = alert.type === 'passport';

            return (
              <Link
                key={`${alert.type}-${alert.id}-${index}`}
                to={
                  isPassport
                    ? `/bookings/${(alert as any).bookingId}`
                    : `/customers/${alert.customer?.id}`
                }
                className="flex items-center justify-between px-6 py-3 hover:bg-[hsl(var(--muted))/30] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`rounded-lg p-2 ${
                      isUrgent ? 'bg-red-100' : 'bg-amber-100'
                    }`}
                  >
                    {isPassport ? (
                      <BookOpen
                        className={`h-4 w-4 ${isUrgent ? 'text-red-600' : 'text-amber-600'}`}
                      />
                    ) : (
                      <FileText
                        className={`h-4 w-4 ${isUrgent ? 'text-red-600' : 'text-amber-600'}`}
                      />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">
                      {isPassport
                        ? (alert as any).passengerName
                        : alert.customer?.name || t('unknownCustomer')}
                    </p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">
                      {isPassport
                        ? `${t('passport')}: ${(alert as any).passportNumber || t('notProvided')}`
                        : `${t((alert as any).documentType?.toLowerCase() || 'document')}: ${(alert as any).documentNumber || t('notProvided')}`}
                    </p>
                  </div>
                </div>
                <div className="text-right ml-4">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
                      isUrgent
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : 'bg-amber-50 text-amber-700 border border-amber-200'
                    }`}
                  >
                    {isUrgent && <AlertTriangle className="h-3 w-3" />}
                    {alert.daysUntilExpiry} {t('daysLeft')}
                  </span>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                    {alert.expiryDate
                      ? new Date(alert.expiryDate).toLocaleDateString(dateLocale, {
                          month: 'short',
                          day: 'numeric',
                        })
                      : ''}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {data && data.totalAlerts > 5 && (
        <div className="border-t border-[hsl(var(--border))] px-6 py-3">
          <Link
            to="/customers?filter=expiring-documents"
            className="flex items-center justify-center gap-2 text-sm font-medium text-[hsl(var(--gold))] hover:underline"
          >
            {t('viewAllDocuments')} ({data.totalAlerts})
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </div>
  );
}
